import {
  Box,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventCard from "../Dashboard/Talent/Card/Featured_Card/EventCard";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTalentListStageCards } from "../../services/stagemode_api/talent/talent_api";
import { useCurrentTalentProfile } from "../../hooks";

const StageCards = ({ talentId }: { talentId: string }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const currentProfile = useCurrentTalentProfile();

  const scrollBy = (offset: number) => {
    scrollRef.current?.scrollBy({ left: offset, behavior: "smooth" });
  };

  const { data } = useTalentListStageCards(talentId ?? "");
  const events = data?.data || [];

  // Netflix-ish tokens (visual only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.78)",
    border: "rgba(255,255,255,0.12)",
    red: "#E50914",
    redHover: "#b20710",
  };

  return (
    <Box
      px={{ xs: 2, sm: 3, md: 4 }}
      sx={{
        background: COLORS.bg,
        color: COLORS.text,
        borderRadius: 3,
        py: { xs: 3, sm: 4 },
        position: "relative",
        overflow: "hidden",
        // subtle vignette glow
        "&:before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(900px 360px at 8% 0%, rgba(229,9,20,.08), transparent 60%)",
          pointerEvents: "none",
        },
      }}
    >
      {/* Header: Title + Arrows */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: { xs: 2, sm: 3, md: 4 }, gap: 1.5 }}
      >
        <Typography
          sx={{
            fontFamily: "Lexend, sans-serif",
            fontWeight: 900,
            fontSize: { xs: 18, sm: 22, md: 24 },
            letterSpacing: "-0.01em",
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            color: COLORS.text,
            "&::after": {
              content: '""',
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: COLORS.red,
              boxShadow: "0 0 12px rgba(229,9,20,0.6)",
              display: "inline-block",
            },
          }}
        >
          {currentProfile ? "My Stage Cards" : "Available Events"}
        </Typography>

        <Stack direction="row" spacing={1}>
          <IconButton
            onClick={() => scrollBy(-400)}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              color: COLORS.text,
              backgroundColor: "rgba(255,255,255,0.06)",
              border: `1px solid ${COLORS.border}`,
              backdropFilter: "blur(6px)",
              p: { xs: 0.5, sm: 0.75 },
              transition: "all .2s ease",
              "&:hover": {
                backgroundColor: COLORS.red,
                borderColor: COLORS.red,
                transform: "scale(1.05)",
                boxShadow: "0 6px 18px rgba(229,9,20,0.35)",
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }} />
          </IconButton>
          <IconButton
            onClick={() => scrollBy(400)}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              color: COLORS.text,
              backgroundColor: "rgba(255,255,255,0.06)",
              border: `1px solid ${COLORS.border}`,
              backdropFilter: "blur(6px)",
              p: { xs: 0.5, sm: 0.75 },
              transition: "all .2s ease",
              "&:hover": {
                backgroundColor: COLORS.red,
                borderColor: COLORS.red,
                transform: "scale(1.05)",
                boxShadow: "0 6px 18px rgba(229,9,20,0.35)",
              },
            }}
          >
            <ChevronRightIcon
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            />
          </IconButton>
        </Stack>
      </Stack>

      {/* Scrollable Events Container with edge fades */}
      <Box sx={{ width: "100%", position: "relative", overflow: "hidden" }}>
        {/* left fade */}
        <Box
          sx={{
            pointerEvents: "none",
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: { xs: 16, sm: 24, md: 32 },
            background:
              "linear-gradient(90deg, rgba(11,11,11,1), rgba(11,11,11,0))",
            zIndex: 1,
          }}
        />
        {/* right fade */}
        <Box
          sx={{
            pointerEvents: "none",
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: { xs: 16, sm: 24, md: 32 },
            background:
              "linear-gradient(270deg, rgba(11,11,11,1), rgba(11,11,11,0))",
            zIndex: 1,
          }}
        />

        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            gap: { xs: 2, sm: 3 },
            overflowX: "auto",
            scrollBehavior: "smooth",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            pb: 1,
          }}
        >
          {events.length > 0 ? (
            events.map((event: any) => (
              <Box
                key={event.id}
                onClick={() => navigate(`/featured-event/${event.id}`)}
                sx={{
                  flex: "0 0 auto",
                  minWidth: { xs: 180, sm: 220, md: 260 },
                  maxWidth: { xs: 220, sm: 260, md: 300 },
                  transition: "transform .3s ease, box-shadow .3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
                  },
                }}
              >
                <EventCard event={event} />
              </Box>
            ))
          ) : (
            <Typography
              sx={{
                color: COLORS.muted,
                px: 2,
                py: 6,
                fontFamily: "Lexend, sans-serif",
              }}
            >
              No events found
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default StageCards;
